import {
  FieldCheckbox,
  FieldColorPicker,
  FieldNumber,
  FieldSegmented,
  FieldSelect,
  genGroupField,
} from 'components/formKit'
import { t } from 'services/Translation'

import { FONT_ITALIC_PARAMS, FONT_SIZE_OPTIONS, LINE_HEIGHT_OPTIONS } from '../constants/constants'
import { genBrandField } from '../genSchema'
import {
  useShowDesktop,
  useBrandColorOptions,
  useFontFamilyOptions,
  useFontWeightOptions,
} from './useFieldConfigs'

const FONT_FAMILY = genBrandField({
  as: FieldSelect<string>,
  label: t('input.label.font'),
  props: { fluid: true, dropdownType: 'dropdown', isSearch: true },
  useConfigs: [useFontFamilyOptions, useShowDesktop],
})

const FONT_SIZE = genBrandField({
  as: FieldSelect<number>,
  label: t('input.label.fontSize'),
  props: {
    options: FONT_SIZE_OPTIONS,
    inputable: true,
    hideEmptyList: true,
    inputType: 'number',
    minWidth: 64,
  },
})

const LINE_HEIGHT = genBrandField({
  as: FieldSelect<string | number>,
  label: t('input.label.lineHeight'),
  props: {
    options: LINE_HEIGHT_OPTIONS,
    inputable: true,
    inputType: 'number',
    hideEmptyList: true,
    minWidth: 64,
  },
})

const COLOR = genBrandField({
  as: FieldColorPicker<string>,
  label: t('input.label.color'),
  useConfigs: [useBrandColorOptions, useShowDesktop],
})

const FONT_STYLE_GROUP = genGroupField({
  label: t('input.label.fontStyle'),
  useConfigs: [useShowDesktop],
})

const FONT_WEIGHT = genBrandField({
  as: FieldSelect<number>,
  props: { fluid: true, isSearch: true, minWidth: 134 },
  useConfigs: [useFontWeightOptions],
})

const FONT_STYLE = genBrandField({
  as: FieldCheckbox<'italic' | 'normal'>,
  props: FONT_ITALIC_PARAMS,
})

const SPACING = genBrandField({
  as: FieldNumber,
  label: t('input.label.spacing'),
  props: { width: 64 },
})

const GAP = genBrandField({
  as: FieldNumber,
  label: t('input.label.gap'),
  props: { width: 64 },
})

const BORDER_LEFT_COLOR = genBrandField({
  as: FieldColorPicker<string>,
  label: t('input.label.delimiter'),
  props: { labeled: true },
  useConfigs: [useBrandColorOptions, useShowDesktop],
})

const HOVER_COLOR = genBrandField({
  as: FieldColorPicker<string | null>,
  label: t('style.hoverColor'),
  props: { transparent: true, labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions, useShowDesktop],
})

const UNDERLINE = genBrandField({
  as: FieldSegmented<'none' | 'underline'>,
  label: t('input.label.textDecoration'),
  props: {
    options: [
      { value: 'none', label: t('common.no') },
      { value: 'underline', label: t('common.yes') },
    ],
  },
  useConfigs: [useShowDesktop],
})

export const FONT_BASE_FIELDS = {
  fontFamily: FONT_FAMILY,
  fontStyleGroup: FONT_STYLE_GROUP,
  fontWeight: FONT_WEIGHT,
  fontStyle: FONT_STYLE,
  fontSize: FONT_SIZE,
  lineHeight: LINE_HEIGHT,
  color: COLOR,
}

export const FONT_LINK_FIELDS = {
  color: COLOR,
  hoverColor: HOVER_COLOR,
  textDecoration: UNDERLINE,
}

export const FONT_LIST_FIELDS = {
  color: COLOR,
  fontStyle: FONT_STYLE,
  fontWeight: FONT_WEIGHT,
  fontStyleGroup: FONT_STYLE_GROUP,
  gap: GAP,
  spacing: SPACING,
}

export const FONT_QUOTE_FIELDS = {
  borderLeftColor: BORDER_LEFT_COLOR,
  fontStyle: FONT_STYLE,
  fontWeight: FONT_WEIGHT,
  fontStyleGroup: FONT_STYLE_GROUP,
  color: COLOR,
  lineHeight: LINE_HEIGHT,
}

export const FONT_FIELDS = {
  base: FONT_BASE_FIELDS,
  h1: FONT_BASE_FIELDS,
  h2: FONT_BASE_FIELDS,
  h3: FONT_BASE_FIELDS,
  h4: FONT_BASE_FIELDS,
  caption: FONT_BASE_FIELDS,
  link: FONT_LINK_FIELDS,
  list: FONT_LIST_FIELDS,
  quote: FONT_QUOTE_FIELDS,
}
